import { Injectable } from '@angular/core';
import { LoggerService } from '@core/services/logger/logger.service';
import { Tooth } from '@modules/teeth-diagram/models/tooth';
import { TeethDiagramQuery } from '@modules/teeth-diagram/state/teeth-diagram-query';
import { TeethDiagramStore } from '@modules/teeth-diagram/state/teeth-diagram-store';
import { Observable } from 'rxjs';
import { IntuitiveIndication, toUnitTypeOrDefault } from '../../../models/intuitive-indication';
import { ToothModalResult } from '../../../models/tooth-modal';
import { RxIntuitiveBridgeService } from '../../rx-intuitive-bridge-service';
import { RxIntuitiveTeethDiagramQuery } from '../../../state/rx-intuitive-teeth-diagram-query';
import { RxIntuitiveTeethDiagramStore } from '../../../state/rx-intuitive-teeth-diagram-store';
import { RxIntuitiveIndicationMapperService } from '../../rx-intuitive-indication-mapper.service';
import { RxIntuitiveToothEditorRoter } from '../../rx-intuitive-tooth-editor-roter.service';
import { IndicationOperations } from '../indication-context.token';

/**
 * Implements {@link IndicationOperations} for the standard (non-bridge) teeth diagram context.
 * Provided in {@link RxIntuitiveTeethOrchestratorContainerComponent} and consumed by
 * {@link DefaultIndicationStrategy} and {@link ImplantIndicationStrategy} via the
 * {@link INDICATION_OPERATIONS} token.
 */
@Injectable()
export class IntuitiveIndicationService implements IndicationOperations {
	private readonly moduleName = 'IntuitiveIndicationService';

	constructor(
		private readonly intuitiveQuery: RxIntuitiveTeethDiagramQuery,
		private readonly intuitiveStore: RxIntuitiveTeethDiagramStore,
		private readonly teethDiagramQuery: TeethDiagramQuery,
		private readonly teethDiagramStore: TeethDiagramStore,
		private readonly indicationMapper: RxIntuitiveIndicationMapperService,
		private readonly bridgeService: RxIntuitiveBridgeService,
		private readonly editorRouting: RxIntuitiveToothEditorRoter,
		private readonly logger: LoggerService
	) {}

	assignIndication(indicationId: IntuitiveIndication, skipResetTooth = false): void {
		const selectedToothIds = this.intuitiveQuery.selectedToothIds;

		if (selectedToothIds.length === 0) {
			this.logger.warn('assignIndication called with no teeth selected', {
				module: this.moduleName
			});

			return;
		}

		if (indicationId === IntuitiveIndication.Bridge) {
			const bridge = this.bridgeService.createBridgeTeethSpan(selectedToothIds, this.teethDiagramQuery.teeth);

			if (bridge) {
				this.intuitiveStore.selectBridge(bridge);
				this.intuitiveStore.markRecentlyEditedTeeth(bridge.teeth.map(t => t.ToothID).filter((id): id is number => id != null));
			}

			return;
		}

		const unitType = toUnitTypeOrDefault(indicationId, this.indicationMapper.getDefaultUnitType());

		if (skipResetTooth) {
			this.teethDiagramStore.updateTeethByIds(selectedToothIds, { UnitTypeID: unitType });

			return;
		}

		this.teethDiagramStore.resetIndicationByIds(selectedToothIds, unitType);
		this.intuitiveStore.markRecentlyEditedTeeth(selectedToothIds);
	}

	openModalForUnitTypeIfNeeded(indicationId: IntuitiveIndication): Observable<ToothModalResult> | null {
		return this.editorRouting.openModalForIndicationIfNeeded(indicationId, {
			onSave: (toothIds: number[], partialUpdate: Partial<Tooth>, indication: IntuitiveIndication) => {
				const unitType = toUnitTypeOrDefault(indication, this.indicationMapper.getDefaultUnitType());
				// eslint-disable-next-line @typescript-eslint/no-unused-vars
				const { ToothID: _, ...updateWithoutId } = partialUpdate as Partial<Tooth> & { ToothID?: never };

				this.teethDiagramStore.updateTeethByIds(toothIds, { UnitTypeID: unitType, ...updateWithoutId });
				this.intuitiveStore.markRecentlyEditedTeeth(toothIds);
			},
			onDelete: (toothIds: number[]) => {
				this.teethDiagramStore.resetIndicationByIds(toothIds, this.indicationMapper.getDefaultUnitType());
				this.intuitiveStore.markRecentlyEditedTeeth(toothIds);
			}
		});
	}
}
